/**
 * Emissions Dashboard Page
 *
 * Overview of carbon emissions with scope breakdown and totals.
 * Uses shadcn/ui components with Tailwind CSS.
 */

import { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { EmissionsKPICards } from '../components/EmissionsKPICards';
import { EmissionsTrendChart } from '../components/EmissionsTrendChart';
import { ScopeBreakdownChart } from '../components/ScopeBreakdownChart';
import { DataQualityCard } from '../components/DataQualityCard';
import { CategoryBreakdownPanel } from '../components/CategoryBreakdownPanel';
import { AssetBreakdownPanel } from '../components/AssetBreakdownPanel';
import { RecentActivitiesCard } from '../components/RecentActivitiesCard';
import { SetTargetModal } from '../components/SetTargetModal';
import {
  useGetEmissionsSummaryQuery,
  useGetOnboardingStatusQuery,
  useUpdateOnboardingStatusMutation,
} from '../api/emissions';
import { OnboardingStatus } from '../types/emissions';
import { OnboardingChecklist } from '../components/onboarding/OnboardingChecklist';
import { WelcomeCard } from '../components/onboarding/WelcomeCard';
import { CreateActivityModal } from '../components/form/CreateActivityModal';
import { DataMethod } from '../components/form/types';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl, generateUrlWithQuery } from '@routes/util';
import {
  Target,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { IncludeChildrenToggle } from '../components/IncludeChildrenToggle';
import { FiscalYearSelector, getFiscalYearPeriod, getPreviousYearPeriod } from '../components/FiscalYearSelector';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { Button } from '@g17eco/ui-modern/components/ui/button';

export const EmissionsDashboardPage = () => {
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  // When set, `CreateActivityModal` is rendered with this DataMethod as its
  // entry point. Null means closed. Triggered from the onboarding checklist,
  // the dashboard "+ Activity" header button, and the Recent Activities empty state.
  const [createFlow, setCreateFlow] = useState<DataMethod | null>(null);
  // Default to the most common single-activity path when no method is specified —
  // e.g. the dashboard header "+ Activity" button. Checklist rows pass an explicit method.
  const openCreateFlow = (method: DataMethod = DataMethod.ByActivity) => setCreateFlow(method);
  const closeCreateFlow = () => setCreateFlow(null);
  const { hasChildren, selectedFiscalYear, setSelectedFiscalYear, availableYears, includeChildren, setIncludeChildren } = useEmissionsFilters();
  const history = useHistory();

  const periodDates = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);
  const previousPeriodDates = useMemo(() => getPreviousYearPeriod(selectedFiscalYear), [selectedFiscalYear]);

  const toggleTargetModal = () => setIsTargetModalOpen((prev) => !prev);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useGetEmissionsSummaryQuery(
    {
      initiativeId,
      periodStart: periodDates.start,
      periodEnd: periodDates.end,
      includeChildren,
    },
    { skip: !initiativeId }
  );

  const {
    data: previousYearSummary,
    isLoading: previousYearLoading,
  } = useGetEmissionsSummaryQuery(
    {
      initiativeId,
      periodStart: previousPeriodDates.start,
      periodEnd: previousPeriodDates.end,
      includeChildren,
    },
    { skip: !initiativeId }
  );

  // Onboarding — drives the first-run UX. Refetches on dashboard mount so the
  // checklist updates when the user returns after completing a step elsewhere.
  const { data: onboarding } = useGetOnboardingStatusQuery(
    { initiativeId },
    { skip: !initiativeId, refetchOnMountOrArgChange: true }
  );
  const [updateOnboardingStatus, { isLoading: isUpdatingOnboarding }] = useUpdateOnboardingStatusMutation();

  const handleStartOnboarding = () => {
    if (!initiativeId) return;
    updateOnboardingStatus({ initiativeId, status: OnboardingStatus.InProgress });
  };

  const handleCompleteOnboarding = () => {
    if (!initiativeId) return;
    updateOnboardingStatus({ initiativeId, status: OnboardingStatus.Completed });
  };

  // Three dashboard modes:
  // - welcome: status === 'not-started' AND no emissions data yet
  // - checklist: status === 'in-progress' — visible regardless of emissions
  //   data so completing "Add first activity" does not hide the remaining steps
  // - normal: status === 'completed', OR legacy users with pre-existing data
  //   (status === 'not-started' AND totalTonnesCO2e > 0) who skip onboarding
  const totalTonnes = summary?.totalTonnesCO2e ?? 0;
  const onboardingStatus = onboarding?.status;
  const showWelcome = onboardingStatus === OnboardingStatus.NotStarted && totalTonnes === 0;
  const showChecklist = onboardingStatus === OnboardingStatus.InProgress;
  const showOnboardingView = showWelcome || showChecklist;

  const activitiesUrl = generateUrlWithQuery(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { rootAppPath, initiativeId }, {
    sort: 'periodStart',
    sortDir: 'desc',
    periodStart: periodDates.start,
    periodEnd: periodDates.end,
    includeChildren: String(includeChildren),
  });

  const scopeUrls = {
    scope1: generateUrl(emissionsTrackerRoutes.EMISSIONS_SCOPE1, { rootAppPath, initiativeId }),
    scope2: generateUrl(emissionsTrackerRoutes.EMISSIONS_SCOPE2, { rootAppPath, initiativeId }),
    scope3: generateUrl(emissionsTrackerRoutes.EMISSIONS_SCOPE3, { rootAppPath, initiativeId }),
  };

  const newActivityUrl = generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, { rootAppPath, initiativeId });

  const errorMessage = summaryError
    ? 'Unable to load emissions data. Please try again.'
    : undefined;

  if (showOnboardingView) {
    return (
      <Dashboard>
        <div className='tw:mb-6'>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Overview</h4>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Let&apos;s get your organisation set up for emissions tracking.
          </p>
        </div>

        {showWelcome ? (
          <WelcomeCard onStart={handleStartOnboarding} isStarting={isUpdatingOnboarding} />
        ) : null}

        {showChecklist && onboarding ? (
          <OnboardingChecklist
            initiativeId={initiativeId}
            items={onboarding.items}
            onOpenTargetModal={toggleTargetModal}
            onOpenCreateFlow={openCreateFlow}
            onDismiss={handleCompleteOnboarding}
            isDismissing={isUpdatingOnboarding}
          />
        ) : null}

        <SetTargetModal
          isOpen={isTargetModalOpen}
          toggle={toggleTargetModal}
          initiativeId={initiativeId}
          currentEmissions={summary?.totalTonnesCO2e ? summary.totalTonnesCO2e * 1000 : undefined}
        />

        {createFlow ? (
          <CreateActivityModal
            isOpen
            toggle={closeCreateFlow}
            initiativeId={initiativeId}
            initialMethod={createFlow}
          />
        ) : null}
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Overview</h4>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Track and manage your organization&apos;s carbon footprint
          </p>
        </div>
        <div className='tw:flex tw:gap-2 tw:items-center'>
          <IncludeChildrenToggle disabled={!hasChildren} includeChildren={includeChildren} onChange={setIncludeChildren} />

          <FiscalYearSelector selectedYear={selectedFiscalYear} onYearChange={setSelectedFiscalYear} years={availableYears} />

          {/* Set Target Button */}
          <Button
            variant='emerald'
            onClick={toggleTargetModal}
            className='tw:text-white tw:border-0'
          >
            <Target />
            Target
          </Button>
          <Button
            variant='default'
            onClick={() => history.push(newActivityUrl)}
            className='tw:text-white tw:border-0'
          >
            <Plus />
            Activity
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {errorMessage && (
        <div className='tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-3 tw:mb-6 tw:text-sm tw:text-amber-800'>
          <AlertTriangle className='tw:size-4 tw:shrink-0' />
          {errorMessage}
        </div>
      )}

      {/* KPI Cards Row */}
      <div className='tw:mb-6'>
        <EmissionsKPICards
          summary={summary}
          previousSummary={previousYearSummary}
          isLoading={summaryLoading}
          previousLoading={previousYearLoading}
          scopeUrls={scopeUrls}
        />
      </div>

      {/* Charts Row */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-12 tw:gap-6 tw:mb-6'>
        <div className='tw:lg:col-span-7'>
          <EmissionsTrendChart
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        </div>
        <div className='tw:lg:col-span-5'>
          <ScopeBreakdownChart initiativeId={initiativeId} summary={summary} isLoading={summaryLoading} />
        </div>
      </div>

      {/* Activity Type Breakdown */}
      <div className='tw:mb-6'>
        <CategoryBreakdownPanel
          initiativeId={initiativeId}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
        />
      </div>

      {/* Asset Type Breakdown */}
      <div className='tw:mb-6'>
        <AssetBreakdownPanel
          initiativeId={initiativeId}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
          variant='donut'
        />
      </div>

      {/* Recent Activities + Data Quality */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-12 tw:gap-6 tw:mb-6'>
        <div className='tw:lg:col-span-8'>
          <RecentActivitiesCard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
            viewAllUrl={activitiesUrl}
            emptyAction={{
              label: 'Add activity',
              to: newActivityUrl,
              icon: <Plus className='tw:size-3.5' />,
            }}
          />
        </div>
        <div className='tw:lg:col-span-4'>
          <DataQualityCard
            initiativeId={initiativeId}
            periodStart={periodDates.start}
            periodEnd={periodDates.end}
            includeChildren={includeChildren}
          />
        </div>
      </div>

      {/* Set Target Modal */}
      <SetTargetModal
        isOpen={isTargetModalOpen}
        toggle={toggleTargetModal}
        initiativeId={initiativeId}
        currentEmissions={summary?.totalTonnesCO2e ? summary.totalTonnesCO2e * 1000 : undefined}
      />

      {createFlow ? (
        <CreateActivityModal
          isOpen
          toggle={closeCreateFlow}
          initiativeId={initiativeId}
          initialMethod={createFlow}
        />
      ) : null}
    </Dashboard>
  );
};
